import { ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import { PanelType } from '@aurora/shared-client/components/common/Panel/enums';
import {
  merge,
  UndefinedValueMergeBehavior
} from '@aurora/shared-utils/helpers/objects/ObjectHelper';
import React, { useContext, useState } from 'react';
import { MessageTimestamp, MessageViewVariant } from '../../../types/enums';
import type { WidgetFC } from '../../common/Widget/types';
import type { IdeaSearchBeforePostWidgetProps } from '../IdeaSearchBeforePostWidgetEditor/types';
import EditableWidget from '../../common/Widget/EditableWidget';
import EditContext from '../../context/EditContext/EditContext';
import PaneledItemListWrapper, {
  PaneledItemListBody,
  PaneledItemListHeader
} from '../../common/List/PaneledItemList/PaneledItemListWrapper';
import IdeaSearchBeforePostSearchBar from './IdeaSearchBeforePostSearchBar';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './IdeaSearchBeforePostWidget.module.pcss';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import IdeaSearchBeforePostSampleResults from './IdeaSearchBeforePostSampleResults';
import IdeaSearchBeforePostSearchResults from './IdeaSearchBeforePostSearchResults';

const defaultProps: IdeaSearchBeforePostWidgetProps = {
  style: 'list',
  listVariant: {
    type: ListVariant.LIST_GROUP
  },
  pagerVariant: {
    type: PagerVariant.LOAD_MORE
  },
  viewVariant: {
    type: MessageViewVariant.INLINE,
    props: {
      useBoardIcon: false,
      useBody: true,
      useNode: false,
      useTags: true,
      useKudosCount: true,
      useViewCount: true,
      useUnreadCount: false,
      useRepliesCount: true,
      useAvatar: false,
      clampSubjectLines: 1,
      clampBodyLines: 3,
      useAuthorLogin: true,
      useAuthorLoginLink: true,
      useAuthorRank: true,
      useSolvedBadge: false,
      usePreviewMedia: false,
      timeStampType: MessageTimestamp.POST_TIME,
      useMessageTimeLink: true,
      useTextBody: true,
      useVideoPreview: false,
      useVoteComponent: false,
      useMessageStatus: true,
      useSearchSnippet: false
    }
  },
  pageSize: 5,
  showSampleResults: false,
  panelType: PanelType.DIVIDER,
  lazyLoad: false,
  enablePagination: true
};

/**
 * final props merged with the props passed to component with default props
 * @param props default props for component
 */
export function getFinalProps(
  props: IdeaSearchBeforePostWidgetProps
): IdeaSearchBeforePostWidgetProps {
  return merge(defaultProps, props, {
    undefinedMergeBehavior: UndefinedValueMergeBehavior.IGNORE_BEFORE_MERGE,
    mergeNested: false
  });
}

/**
 * Idea search before post widget
 *
 * @author rishabh.jivan
 */
const IdeaSearchBeforePostWidget: WidgetFC<IdeaSearchBeforePostWidgetProps> = props => {
  const { isVisible, ...rest } = props;
  const finalProps: IdeaSearchBeforePostWidgetProps = getFinalProps(rest);
  const { panelType, showSampleResults } = finalProps;
  const { showEditControls: showPageEditControls } = useContext(EditContext);
  const cx = useClassNameMapper(localStyles);
  const [queryTerm, setQueryTerm] = useState('');
  const isInPreviewMode = showPageEditControls && showSampleResults;
  const i18n = useTranslation(EndUserComponent.IDEA_SEARCH_BEFORE_POST_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;

  /**
   * Handler for when search term is entered in the search bar component
   * @param searchTerm the user entered search term
   */
  const onSearchChanged = (searchTerm: string) => {
    setQueryTerm(searchTerm);
  };

  /**
   * Function to return the intro text element for displaying an intro text
   * above the search results for when there are results to display.
   * @returns dom element containing the intro text
   */
  function renderIntroText(): React.ReactElement {
    return (
      <PaneledItemListBody>
        <h6
          className={cx('text-break text-center', panelType === PanelType.BUBBLE && 'lia-g-mt-20')}
        >
          {formatMessage('introText')}
        </h6>
      </PaneledItemListBody>
    );
  }

  const footerClassname = cx(
    'text-center',
    { 'lia-idea-search-footer': panelType !== PanelType.BUBBLE },
    { 'bg-transparent p-0': panelType === PanelType.BUBBLE }
  );
  const pagerClassname = cx(
    { 'lia-g-mb-20': panelType !== PanelType.BUBBLE },
    {
      'lia-panel-footer-item lia-g-mb-10': panelType === PanelType.BUBBLE
    }
  );
  const actionPanelClassname = cx({ 'lia-panel-footer-item': panelType === PanelType.BUBBLE });
  const actionPanelIntroClassname = cx('lia-g-mb-20');

  if (textLoading) {
    return null;
  }

  return (
    <EditableWidget<IdeaSearchBeforePostWidgetProps> props={finalProps}>
      <PaneledItemListWrapper
        className={cx('lia-panel-wrapper', `lia-panel-wrapper-${panelType}`)}
        type={panelType}
      >
        <PaneledItemListHeader className={cx('lia-panel-wrapper-header')}>
          <h3 className={cx('sr-only')}>{formatMessage('searchFieldPlaceholderText')}</h3>
          <IdeaSearchBeforePostSearchBar defaultSearchTerm={queryTerm} onChange={onSearchChanged} />
        </PaneledItemListHeader>
        {isInPreviewMode === true && (
          <IdeaSearchBeforePostSampleResults
            widgetProps={finalProps}
            introTextElement={renderIntroText}
            footerClassname={footerClassname}
            pagerClassname={pagerClassname}
            actionPanelClassname={actionPanelClassname}
            actionPanelIntroClassname={actionPanelIntroClassname}
          />
        )}
        {showPageEditControls === false && (
          <IdeaSearchBeforePostSearchResults
            queryTerm={queryTerm}
            widgetProps={finalProps}
            introTextElement={renderIntroText}
            footerClassname={footerClassname}
            pagerClassname={pagerClassname}
            actionPanelClassname={actionPanelClassname}
            actionPanelIntroClassname={actionPanelIntroClassname}
          />
        )}
      </PaneledItemListWrapper>
    </EditableWidget>
  );
};

export default IdeaSearchBeforePostWidget;
